// src/utils/authInterceptors.js
//
// Shared on-demand access-token refresh for axios service instances.
//
// This app has no proactive refresh timer (see sessionManager.js) — instead the
// access token is refreshed lazily, at the moment of an API call, which is both
// cheaper and reliable for backgrounded/frozen tabs (where timers are throttled).
//
// Usage: call attachTokenRefresh(instance) AFTER the instance registers its own
// request interceptor. Axios runs request interceptors in REVERSE registration
// order, so registering last makes this run FIRST — it awaits the refresh, which
// updates the store + sessionStorage, so the instance's own interceptor then
// reads the freshly-minted token regardless of whether it reads from the store
// or sessionStorage.

import axios from 'axios';
import store from '@/store';
import { ensureFreshToken } from './sessionManager';

/**
 * Prepend an on-demand token-refresh step to an axios instance's request chain.
 * @param {import('axios').AxiosInstance} instance
 * @returns {import('axios').AxiosInstance} the same instance (for chaining).
 */
export function attachTokenRefresh(instance) {
  instance.interceptors.request.use(
    async (config) => {
      await ensureFreshToken();
      return config;
    },
    (error) => Promise.reject(error)
  );
  return instance;
}

let globalInstalled = false;

/**
 * Install on-demand refresh + fresh auth headers on the GLOBAL default axios
 * instance — i.e. direct `axios.get/post(...)` calls that bypass the per-instance
 * interceptors. Historically these relied on a one-time
 * `axios.defaults.headers.common['X-Auth-Token']` set at bootstrap, which went
 * stale after the first token refresh (→ 401s once the original token expired).
 * This refreshes on demand and re-applies the current token on every request.
 *
 * Idempotent. axios.create() instances do NOT inherit default interceptors, so
 * this never double-refreshes the instances wired via attachTokenRefresh().
 */
export function installGlobalAuthRefresh() {
  if (globalInstalled) {
    return;
  }
  globalInstalled = true;
  axios.interceptors.request.use(
    async (config) => {
      await ensureFreshToken();
      const token = store.getters['login/token'];
      const userId = store.getters['login/userId'];
      if (token) {
        config.headers = config.headers || {};
        config.headers['X-Auth-Token'] = token;
        // Re-apply the Bearer Authorization header too. Callers like
        // getServiceConfigHeaders() bake `Authorization: Bearer <token>` into the
        // config BEFORE this interceptor runs, so the token captured there predates
        // ensureFreshToken()'s rotation and may be stale (→ 401 on near-expiry
        // calls). Overwrite only when it's already a Bearer header so we never
        // clobber a different auth scheme.
        const authHeader = config.headers['Authorization'];
        if (typeof authHeader === 'string' && authHeader.startsWith('Bearer ')) {
          config.headers['Authorization'] = `Bearer ${token}`;
        }
        if (userId) {
          config.headers['X-User-Id'] = userId;
        }
      }
      return config;
    },
    (error) => Promise.reject(error)
  );
}
